import { isCreatedOnOrAfterDay, startOfTrendWindow } from './connectionDashboard';
import { newsletterStatusKey } from './newsletterStatus';
import { supabase } from './supabase';

export type PlanningDashboardData = {
  transcriptsLast14Days: number;
  transcriptsNeedProcessing: number;
  contentScheduledFuture: number;
  subscriberCreatedAtValues: string[];
};

type TranscriptRow = {
  created_at?: string | null;
  transcript_chunks_status?: string | null;
};

type NewsletterRow = {
  status?: string | null;
  scheduled_for?: string | null;
};

function needsTranscriptProcessing(status: string | null | undefined): boolean {
  return (status ?? 'none') !== 'ready';
}

function parseScheduledMs(value: string | null | undefined): number | null {
  if (!value?.trim()) return null;
  const ms = new Date(value.trim()).getTime();
  return Number.isNaN(ms) ? null : ms;
}

export async function fetchPlanningDashboardData(): Promise<PlanningDashboardData> {
  const windowStart = startOfTrendWindow(14);
  const nowMs = Date.now();

  const [transcriptsResult, newslettersResult, subscribersResult] = await Promise.all([
    supabase
      .from('transcripts')
      .select('created_at, transcript_chunks_status')
      .limit(5000),
    supabase
      .from('newsletters')
      .select('status, scheduled_for')
      .limit(5000),
    supabase
      .from('subscribers')
      .select('created_at')
      .order('created_at', { ascending: false, nullsFirst: false })
      .limit(20000),
  ]);

  if (transcriptsResult.error) {
    throw new Error(transcriptsResult.error.message || 'Failed to load transcripts');
  }
  if (newslettersResult.error) {
    throw new Error(newslettersResult.error.message || 'Failed to load newsletters');
  }
  if (subscribersResult.error) {
    throw new Error(subscribersResult.error.message || 'Failed to load subscribers');
  }

  const transcripts = (transcriptsResult.data ?? []) as TranscriptRow[];
  const newsletters = (newslettersResult.data ?? []) as NewsletterRow[];
  const subscriberRows = subscribersResult.data ?? [];
  const subscriberCreatedAtValues = subscriberRows
    .map((row) => (typeof row.created_at === 'string' ? row.created_at : ''))
    .filter(Boolean);

  let transcriptsLast14Days = 0;
  let transcriptsNeedProcessing = 0;
  for (const row of transcripts) {
    if (typeof row.created_at === 'string' && isCreatedOnOrAfterDay(row.created_at, windowStart)) {
      transcriptsLast14Days += 1;
    }
    if (needsTranscriptProcessing(row.transcript_chunks_status)) {
      transcriptsNeedProcessing += 1;
    }
  }

  let contentScheduledFuture = 0;
  for (const row of newsletters) {
    const status = newsletterStatusKey(row.status);
    if (status === 'published') continue;

    const sendMs = parseScheduledMs(row.scheduled_for);
    if (sendMs != null && sendMs > nowMs) {
      contentScheduledFuture += 1;
    }
  }

  return {
    transcriptsLast14Days,
    transcriptsNeedProcessing,
    contentScheduledFuture,
    subscriberCreatedAtValues,
  };
}
